import React, { useEffect, useState } from "react";
import { Avatar, Button, Space, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { getRandomColor } from "../../../constants/getRandomColor";
import { UpdateUserInterface } from "../../../services/api/user/types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { userActions } from "../../../store/user";
import userSelectors from "../../../store/user/selectors";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  TrophyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { formatNumber } from "../../../constants/ConstantVar";
import { useNavigate } from "react-router-dom";
import { LeaderboardInterface } from "../../../services/api/referral/types";

interface ReferralLeaderListProps {
  dateRange?: {
    start_date: string;
    end_date: string;
  };
}

const ReferralLeaderList: React.FC<ReferralLeaderListProps> = ({
  dateRange,
}) => {
  const [pageSize, setpageSize] = useState<number>(10);
  const topReferrals = useAppSelector(userSelectors.getTopReferrals);
  const dispatch = useAppDispatch();
  const loader = useAppSelector(userSelectors.getLoader);
  const navigate = useNavigate();
  function manageReferralUser(id: number) {
    navigate("/leaderboard/manage/" + id);
  }
  const { is_staff } = useAppSelector(userSelectors.getUserDetails);

  const paginationProps = {
    showLessItems: true,
    showSizeChanger: true,
    pageSize: pageSize,

    total: topReferrals.count,
    onChange: (pageNumber: number) => {
      dispatch(
        userActions.getTopReferralDetails({
          page: pageNumber,
          pageSize: pageSize,
          start_date: dateRange?.start_date,
          end_date: dateRange?.end_date,
        })
      );
    },
    onShowSizeChange: (current: number, size: number) => {
      setpageSize(size);
      dispatch(
        userActions.getTopReferralDetails({
          page: current,
          pageSize: size,
          start_date: dateRange?.start_date,
          end_date: dateRange?.end_date,
        })
      );
    },
  };
  const customerColumns: ColumnsType<LeaderboardInterface> = [
    {
      key: 1,
      title: "Username",
      render: (referral, _, index) => {
        const medal =
          index === 0 ? (
            <span>{"🥇"}</span>
          ) : index === 1 ? (
            <span>{"🥈"}</span>
          ) : index === 2 ? (
            <span>{"🥉"}</span>
          ) : null;

        return (
          <div>
            <Space direction="horizontal" size={20} className="grey">
              <Avatar
                style={{ backgroundColor: getRandomColor() }}
                size="large"
              >
                {referral?.referrer_user__first_name ||
                referral?.referrer_user__last_name ? (
                  `${referral?.referrer_user__first_name?.[0] ?? ""}${
                    referral?.referrer_user__last_name?.[0] ?? ""
                  }`.toUpperCase()
                ) : (
                  <UserOutlined />
                )}
              </Avatar>
              <Space direction="vertical" size={0}>
                <div className="grey text-16">
                  <span>
                    {referral?.referrer_user__first_name ||
                    referral?.referrer_user__last_name
                      ? `${
                          referral?.referrer_user__first_name
                            ? referral?.referrer_user__first_name
                                .charAt(0)
                                .toUpperCase() +
                              referral?.referrer_user__first_name
                                .slice(1)
                                .toLowerCase()
                            : ""
                        } ${
                          referral?.referrer_user__last_name
                            ? referral?.referrer_user__last_name
                                .charAt(0)
                                .toUpperCase() +
                              referral?.referrer_user__last_name
                                .slice(1)
                                .toLowerCase()
                            : ""
                        }`.trim()
                      : "Unknown User"}
                  </span>
                  {medal}
                </div>
              </Space>
            </Space>
          </div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a.referrer_user__first_name?.localeCompare(
          b?.referrer_user__first_name!
        )!,
    },
    {
      key: 2,
      title: "Weekly Referrals",
      render: (referral) => {
        return <div className="grey">{referral?.weekly_referrals}</div>;
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.weekly_referrals! - b?.weekly_referrals!,
    },
    {
      key: 3,
      title: "Total Referrals",
      render: (referral) => {
        return (
          <div className="grey">{formatNumber(referral?.total_referrals)}</div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.total_referrals! - b?.total_referrals!,
    },
    {
      key: 4,
      title: "Earned [HVT]",
      render: (referral) => {
        return (
          <div className="grey">
            {parseFloat(referral?.referrer_user__total_earned_rewards).toFixed(
              2
            )}
            <sub className="text-primary-color"> HVT</sub>
          </div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.referrer_user__total_earned_rewards! -
        b?.referrer_user__total_earned_rewards!,
    },
  ];
  const adminColumns: ColumnsType<LeaderboardInterface> = [
    {
      key: 1,
      title: "Username",
      render: (referral, _, index) => {
        const medal =
          index === 0 ? (
            <span>{"🥇"}</span>
          ) : index === 1 ? (
            <span>{"🥈"}</span>
          ) : index === 2 ? (
            <span>{"🥉"}</span>
          ) : null;

        return (
          <div>
            <Space direction="horizontal" size={20} className="grey">
              <Avatar
                style={{ backgroundColor: getRandomColor() }}
                size="large"
              >
                {referral?.referrer_user__first_name ||
                referral?.referrer_user__last_name ? (
                  `${referral?.referrer_user__first_name?.[0] ?? ""}${
                    referral?.referrer_user__last_name?.[0] ?? ""
                  }`.toUpperCase()
                ) : (
                  <UserOutlined />
                )}
              </Avatar>
              <Space direction="vertical" size={0}>
                <div className="grey text-16">
                  <span>
                    {referral?.referrer_user__first_name ||
                    referral?.referrer_user__last_name
                      ? `${
                          referral?.referrer_user__first_name
                            ? referral?.referrer_user__first_name
                                .charAt(0)
                                .toUpperCase() +
                              referral?.referrer_user__first_name
                                .slice(1)
                                .toLowerCase()
                            : ""
                        } ${
                          referral?.referrer_user__last_name
                            ? referral?.referrer_user__last_name
                                .charAt(0)
                                .toUpperCase() +
                              referral?.referrer_user__last_name
                                .slice(1)
                                .toLowerCase()
                            : ""
                        }`.trim()
                      : "Unknown User"}
                  </span>
                  {medal}
                </div>
                <div className="profile-text-color text-12">
                  {referral?.referrer_user__email}
                </div>
              </Space>
            </Space>
          </div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a.referrer_user__first_name?.localeCompare(
          b?.referrer_user__first_name!
        )!,
    },
    {
      key: 2,
      title: "Weekly Referrals",
      render: (referral) => {
        return <div className="grey">{referral?.weekly_referrals}</div>;
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.weekly_referrals! - b?.weekly_referrals!,
    },
    {
      key: 3,
      title: "Total Referrals",
      render: (referral) => {
        return (
          <div className="grey">{formatNumber(referral?.total_referrals)}</div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.total_referrals! - b?.total_referrals!,
    },
    {
      key: 4,
      title: "Earned [HVT]",
      render: (referral) => {
        return (
          <div className="grey">
            {parseFloat(referral?.referrer_user__total_earned_rewards).toFixed(
              2
            )}
            <sub className="text-primary-color"> HVT</sub>
          </div>
        );
      },
      sorter: (a: LeaderboardInterface, b: LeaderboardInterface) =>
        a?.referrer_user__total_earned_rewards! -
        b?.referrer_user__total_earned_rewards!,
    },
    {
      key: 5,
      title: "Manage user",
      render: (referral) => {
        return (
          <Button
            className="user-manage-verify-button "
            onClick={() => manageReferralUser(referral.referrer_user__id)}
          >
            Manage
            <RightOutlined />
          </Button>
        );
      },
    },
  ];
  return (
    <Table
      loading={loader}
      sticky={true}
      scroll={{ x: true, y: 525 }}
      pagination={paginationProps}
      columns={is_staff ? adminColumns : customerColumns}
      dataSource={topReferrals?.results}
      rowKey={"referrer_user__id"}
    />
  );
};

export default ReferralLeaderList;
